// Module: multi-image-requirements
// Role: Pure readiness/payload helpers for MultiImageUploadBrick (BUG-1593).
//       Required flags decide readiness; payload keeps only uploaded records
//       on their original indices so kind/role/image_N never shift.
// Uses: api/flow-types:ArtefactItem,ArtefactRequirement,Checkpoint
// Used by: MultiImageUploadBrick.tsx, MultiImageUploadZone.tsx

import type {
  ArtefactItem,
  ArtefactRequirement,
  Checkpoint,
} from '../../../api/flow-types';

const IMAGE_KINDS = new Set(['screenshot', 'real_photo']);

export interface UploadedArtefact {
  artefactPath: string;
}

export function imageRequirements(checkpoint: Checkpoint): ArtefactRequirement[] {
  return checkpoint.artefact_requirements.filter((req) => IMAGE_KINDS.has(req.kind));
}

export function roleFor(req: ArtefactRequirement, index: number): string {
  return req.role ?? `image_${index + 1}`;
}

export function labelFor(req: ArtefactRequirement, index: number): string {
  if (req.label) return req.label;
  if (req.role === 'pvz_status_screenshot') return 'Скриншот статуса получения';
  if (req.role === 'received_product_photo') return 'Фото полученного товара';
  if (req.role === 'damaged_barcode_photo') return 'Фото поврежденного ШК';
  return `Фото ${index + 1}`;
}

export function descriptionFor(req: ArtefactRequirement): string {
  if (req.description) return req.description;
  if (req.kind === 'screenshot') return 'Скриншот из приложения маркетплейса.';
  return 'Фото товара или упаковки.';
}

function isUploaded(uploaded: Record<number, UploadedArtefact>, index: number): boolean {
  return Boolean(uploaded[index]);
}

export function isReadyToSubmit(
  requirements: ArtefactRequirement[],
  uploaded: Record<number, UploadedArtefact>,
): boolean {
  if (requirements.length === 0) return false;
  if (!requirements.some((req) => req.required)) {
    return requirements.some((_, index) => isUploaded(uploaded, index));
  }
  return requirements.every((req, index) => !req.required || isUploaded(uploaded, index));
}

export function nextUploadIndex(
  requirements: ArtefactRequirement[],
  uploaded: Record<number, UploadedArtefact>,
): number {
  const missingRequired = requirements.findIndex(
    (req, index) => req.required && !isUploaded(uploaded, index),
  );
  if (missingRequired >= 0) return missingRequired;
  if (isReadyToSubmit(requirements, uploaded)) return -1;
  return requirements.findIndex((_, index) => !isUploaded(uploaded, index));
}

export function buildArtefacts(
  requirements: ArtefactRequirement[],
  uploaded: Record<number, UploadedArtefact>,
): ArtefactItem[] {
  const artefacts: ArtefactItem[] = [];
  requirements.forEach((req, index) => {
    const item = uploaded[index];
    if (!item) return;
    artefacts.push({
      kind: req.kind,
      role: roleFor(req, index),
      artefact_path: item.artefactPath,
    });
  });
  return artefacts;
}
